import type { RetiredCodeBehaviour, WorkspaceDocument } from '@myplayer/schema';
import { IconEdit, IconSettings } from '@myplayer/ui';
import { doc } from 'firebase/firestore';
import { useEffect, useState, type FormEvent, type ReactElement } from 'react';
import { useAuth } from './auth-context.js';
import { formatWorkspaceName } from './auth-session.js';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardIcon,
  CardRow,
  CardTitle,
  LinearSpinner,
  Tag,
  ToastNotification,
} from './components/ui/primitives.js';
import { getFirebaseClients } from './firebase.js';
import { WORKSPACE_LOCALE_OPTIONS } from './locale-region-options.js';
import { formatCapabilityRestrictionNotice } from './rbac-ui.js';
import { ADMIN_STRINGS, formatPlainAdminError } from './strings.js';
import { resolveDefaultFallbackUrl } from './use-workspace-identifiers.js';
import { WorkspaceSettingsModal } from './settings-modals.js';
import {
  updateWorkspaceConfigurationInFirestore,
  validateWorkspaceDraft,
} from './workspaces-firestore.js';

export {
  updateWorkspaceConfigurationInFirestore,
  type UpdateWorkspaceConfigurationParams,
} from './workspaces-firestore.js';

export interface WorkspaceSettingsSectionProps {
  readonly initialWorkspaceDoc?: Partial<WorkspaceDocument>;
  readonly onSaveWorkspaceConfig?: (params: {
    readonly orgId: string;
    readonly workspaceId: string;
    readonly defaultFallbackUrl: string;
    readonly defaultLocale: string;
    readonly retiredCodeBehaviour: RetiredCodeBehaviour;
    readonly gs1CompanyPrefixes: readonly string[];
    readonly enforcePrefixValidation: boolean;
  }) => Promise<void>;
}

export function WorkspaceSettingsSection({
  initialWorkspaceDoc,
  onSaveWorkspaceConfig,
}: WorkspaceSettingsSectionProps): ReactElement {
  const { session, can, availableWorkspaces } = useAuth();
  const orgId = session?.orgId ?? '';
  const workspaceId = session?.activeWorkspaceId ?? '';
  const workspaceName =
    availableWorkspaces.find((ws) => ws.workspaceId === workspaceId)?.name ??
    formatWorkspaceName(undefined, workspaceId);
  const canUpdateWorkspace = can('workspace:update');

  const [defaultFallbackUrl, setDefaultFallbackUrl] = useState(
    initialWorkspaceDoc?.defaultFallbackUrl ?? resolveDefaultFallbackUrl(),
  );
  const [defaultLocale, setDefaultLocale] = useState(initialWorkspaceDoc?.defaultLocale ?? 'en-GB');
  const [retiredCodeBehaviour, setRetiredCodeBehaviour] = useState<RetiredCodeBehaviour>(
    initialWorkspaceDoc?.retiredCodeBehaviour ?? 'GONE_410',
  );
  const [prefixesInput, setPrefixesInput] = useState(
    (initialWorkspaceDoc?.gs1CompanyPrefixes ?? []).join(', '),
  );
  const [enforcePrefixValidation, setEnforcePrefixValidation] = useState(
    initialWorkspaceDoc?.enforcePrefixValidation ?? false,
  );
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [draftFallbackUrl, setDraftFallbackUrl] = useState(defaultFallbackUrl);
  const [draftLocale, setDraftLocale] = useState(defaultLocale);
  const [draftRetiredBehaviour, setDraftRetiredBehaviour] =
    useState<RetiredCodeBehaviour>(retiredCodeBehaviour);
  const [draftPrefixesInput, setDraftPrefixesInput] = useState(prefixesInput);
  const [draftEnforcePrefix, setDraftEnforcePrefix] = useState(enforcePrefixValidation);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!orgId || !workspaceId || initialWorkspaceDoc !== undefined) return;
    let unsubscribe: (() => void) | undefined;
    let active = true;
    void (async () => {
      try {
        const { onSnapshot } = await import('firebase/firestore');
        if (!active) return;
        const { db } = getFirebaseClients();
        const wsRef = doc(db, 'organisations', orgId, 'workspaces', workspaceId);
        unsubscribe = onSnapshot(
          wsRef,
          (snap) => {
            if (!snap.exists()) return;
            const data = snap.data() as Partial<WorkspaceDocument>;
            if (typeof data.defaultFallbackUrl === 'string')
              setDefaultFallbackUrl(data.defaultFallbackUrl);
            if (typeof data.defaultLocale === 'string') setDefaultLocale(data.defaultLocale);
            if (data.retiredCodeBehaviour) setRetiredCodeBehaviour(data.retiredCodeBehaviour);
            if (Array.isArray(data.gs1CompanyPrefixes))
              setPrefixesInput(data.gs1CompanyPrefixes.join(', '));
            if (typeof data.enforcePrefixValidation === 'boolean')
              setEnforcePrefixValidation(data.enforcePrefixValidation);
          },
          (err) =>
            setErrorMessage(
              formatPlainAdminError(err, ADMIN_STRINGS.errors.workspaceConfigFallback),
            ),
        );
      } catch {
        // Ignore in non-Firestore test environments
      }
    })();
    return () => {
      active = false;
      if (unsubscribe) unsubscribe();
    };
  }, [orgId, workspaceId, initialWorkspaceDoc]);

  const openEditModal = (): void => {
    setDraftFallbackUrl(defaultFallbackUrl);
    setDraftLocale(defaultLocale);
    setDraftRetiredBehaviour(retiredCodeBehaviour);
    setDraftPrefixesInput(prefixesInput);
    setDraftEnforcePrefix(enforcePrefixValidation);
    setShowAdvanced(false);
    setErrorMessage(null);
    setEditModalOpen(true);
  };

  const closeEditModal = (): void => {
    setEditModalOpen(false);
    setErrorMessage(null);
  };

  const handleSave = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!session || !canUpdateWorkspace) return;
    setErrorMessage(null);
    setFeedbackMessage(null);
    const validation = validateWorkspaceDraft(draftFallbackUrl, draftPrefixesInput);
    if (!validation.ok) {
      setErrorMessage(validation.error);
      return;
    }
    setSubmitting(true);
    try {
      const trimmedUrl = draftFallbackUrl.trim();
      const payload = {
        orgId,
        workspaceId,
        defaultFallbackUrl: trimmedUrl,
        defaultLocale: draftLocale,
        retiredCodeBehaviour: draftRetiredBehaviour,
        gs1CompanyPrefixes: validation.prefixes,
        enforcePrefixValidation: draftEnforcePrefix,
      };
      if (onSaveWorkspaceConfig) {
        await onSaveWorkspaceConfig(payload);
      } else {
        await updateWorkspaceConfigurationInFirestore({
          db: getFirebaseClients().db,
          caller: session.callerIdentity,
          ...payload,
        });
      }
      setDefaultFallbackUrl(trimmedUrl);
      setDefaultLocale(draftLocale);
      setRetiredCodeBehaviour(draftRetiredBehaviour);
      setPrefixesInput(validation.prefixes.join(', '));
      setEnforcePrefixValidation(draftEnforcePrefix);
      setEditModalOpen(false);
      setFeedbackMessage('Workspace resolver configuration saved.');
    } catch (err: unknown) {
      setErrorMessage(formatPlainAdminError(err, ADMIN_STRINGS.errors.workspaceConfigFallback));
    } finally {
      setSubmitting(false);
    }
  };

  const activeLocaleLabel =
    WORKSPACE_LOCALE_OPTIONS.find((opt) => opt.value === defaultLocale)?.label ?? defaultLocale;
  const activePrefixes = prefixesInput
    .split(',')
    .map((p) => p.trim())
    .filter((p) => p.length > 0);

  return (
    <Card className="h-full" data-testid="workspace-settings-card">
      {submitting && <LinearSpinner />}
      <CardHeader>
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          <CardIcon>
            <IconSettings />
          </CardIcon>
          <CardTitle>Resolver Defaults &amp; GS1 Prefix Policy</CardTitle>
          <Badge variant={enforcePrefixValidation ? 'valid' : 'secondary'}>
            {enforcePrefixValidation ? 'Prefix Check On' : 'Prefix Check Off'}
          </Badge>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {canUpdateWorkspace ? (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={submitting}
              data-testid="edit-workspace-settings-btn"
              aria-label="Edit Configuration"
              onClick={openEditModal}
            >
              <IconEdit />
              <span>Edit</span>
            </Button>
          ) : (
            <span
              className="text-[11px] font-medium text-fg-muted"
              data-testid="workspace-config-readonly-notice"
            >
              {formatCapabilityRestrictionNotice({
                capability: 'workspace:update',
                actionSummary: 'update workspace resolver configuration',
                callerRole: session?.personaRole,
              })}
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <CardDescription>
          Default fallback link, language, and GS1 barcode prefix rules.
        </CardDescription>
        <div className="divide-y divide-subtle border-y border-subtle">
          <CardRow label="Fallback web address">
            <span className="truncate font-mono text-[12px]">{defaultFallbackUrl}</span>
          </CardRow>
          <CardRow label="Default language">
            <span className="text-[12px]">{activeLocaleLabel}</span>
          </CardRow>
          <CardRow label="Discontinued products">
            <Tag variant="outline">
              {retiredCodeBehaviour === 'GONE_410'
                ? 'Show page not found (410)'
                : 'Send to fallback web address'}
            </Tag>
          </CardRow>
          <CardRow label="GS1 company prefixes">
            {activePrefixes.length > 0 ? (
              <div className="flex flex-wrap justify-end gap-1.5">
                {activePrefixes.map((prefix) => (
                  <Tag key={prefix} variant="mono" size="sm">
                    {prefix}
                  </Tag>
                ))}
              </div>
            ) : (
              <span className="text-[12px] text-fg-muted">None set</span>
            )}
          </CardRow>
          <CardRow label="Prefix check">
            <Badge variant={enforcePrefixValidation ? 'valid' : 'outline'}>
              {enforcePrefixValidation ? 'Required when publishing' : 'Optional'}
            </Badge>
          </CardRow>
        </div>
        <ToastNotification message={feedbackMessage} onDismiss={() => setFeedbackMessage(null)} />
        {errorMessage !== null && !editModalOpen && (
          <p
            role="alert"
            className="rounded-control border border-status-error-border bg-status-error-bg p-2.5 text-[12px] font-medium text-status-error"
          >
            {errorMessage}
          </p>
        )}
      </CardContent>

      <WorkspaceSettingsModal
        open={editModalOpen}
        workspaceId={workspaceId}
        workspaceName={workspaceName}
        canUpdateWorkspace={canUpdateWorkspace}
        submitting={submitting}
        draftFallbackUrl={draftFallbackUrl}
        setDraftFallbackUrl={setDraftFallbackUrl}
        draftLocale={draftLocale}
        setDraftLocale={setDraftLocale}
        draftRetiredBehaviour={draftRetiredBehaviour}
        setDraftRetiredBehaviour={setDraftRetiredBehaviour}
        draftPrefixesInput={draftPrefixesInput}
        setDraftPrefixesInput={setDraftPrefixesInput}
        draftEnforcePrefix={draftEnforcePrefix}
        setDraftEnforcePrefix={setDraftEnforcePrefix}
        showAdvanced={showAdvanced}
        setShowAdvanced={setShowAdvanced}
        errorMessage={errorMessage}
        onClose={closeEditModal}
        onSubmit={(e) => void handleSave(e)}
      />
    </Card>
  );
}
